import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { IntegrationsTable } from '@/components/admin/integrations/integrations-table';
import { OrganizationPicker } from '@/components/admin/shared/organization-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminIntegrations } from '@/hooks/data/admin/use-integrations';
import { Platform, PlatformConnectionStatus } from '@/types/generated/enums';
import type {
  Platform as PlatformType,
  PlatformConnectionStatus as PlatformConnectionStatusType,
} from '@/types/generated';

const STATUS_VALUES = Object.values(PlatformConnectionStatus);

export const searchSchema = z.object({
  platform: z.nativeEnum(Platform).optional(),
  // A bookmarked filter on a status that no longer exists drops the filter, not the page.
  status: z
    .string()
    .optional()
    .transform((v) => STATUS_VALUES.find((s) => s === v)),
  organization_id: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/integrations')({
  validateSearch: searchSchema,
  staticData: { title: 'Integrations' },
  component: AdminIntegrationsPage,
});

function AdminIntegrationsPage(): React.ReactElement {
  const { platform, status, organization_id, offset } = Route.useSearch();
  const navigate = Route.useNavigate();

  const integrationsQuery = useAdminIntegrations({
    platform,
    status,
    organization_id,
    limit: LIMIT,
    offset,
  });

  const setOrgId = (value: number | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, organization_id: value, offset: 0 }),
    });
  };

  const setPlatform = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        platform: value === 'ALL' ? undefined : (value as PlatformType),
        offset: 0,
      }),
    });
  };

  const setStatus = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        status:
          value === 'ALL' ? undefined : (value as PlatformConnectionStatusType),
        offset: 0,
      }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="Integrations"
        description="Manage platform connections across all organizations"
      />
      <FilterBar>
        <OrganizationPicker value={organization_id} onChange={setOrgId} />
        <Select value={platform ?? 'ALL'} onValueChange={setPlatform}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All platforms</SelectItem>
            <SelectItem value={Platform.GOOGLE}>Google</SelectItem>
            <SelectItem value={Platform.META}>Meta</SelectItem>
            <SelectItem value={Platform.AMAZON}>Amazon</SelectItem>
            <SelectItem value={Platform.TIKTOK}>TikTok</SelectItem>
            <SelectItem value={Platform.SHOPIFY}>Shopify</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status ?? 'ALL'} onValueChange={setStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value={PlatformConnectionStatus.CONNECTED}>
              Connected
            </SelectItem>
            <SelectItem value={PlatformConnectionStatus.EXPIRED}>
              Expired
            </SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <IntegrationsTable
        query={integrationsQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
      />
    </main>
  );
}
